import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useMyParticipants, useUpcomingSessions } from "../lib/queries";
import { SessionCard } from "../components/SessionCard";
import { ErrorBox, Loading } from "../components/Loading";
import { fmtDayLabel, dateKey } from "../lib/format";

export default function Feed() {
  const { user } = useAuth();
  const sessionsQ = useUpcomingSessions();
  const kidsQ = useMyParticipants(user?.parent_id ?? undefined);

  if (sessionsQ.isLoading) return <Loading />;
  if (sessionsQ.error) return <ErrorBox message={(sessionsQ.error as Error).message} />;

  const sessions = sessionsQ.data ?? [];
  const kids = kidsQ.data ?? [];
  const kidsById = new Map(kids.map((k) => [k.id, k]));
  const kidsWithoutCoach = kids.filter((k) => (k.coaches?.length ?? 0) === 0);

  if (sessions.length === 0) {
    return (
      <Wrapper>
        {kids.length === 0 ? (
          <div className="card text-center">
            <div className="text-3xl mb-2">👶</div>
            <h3 className="font-semibold mb-1">Спочатку додай дитину</h3>
            <p className="text-sm text-tg-hint mb-3">
              Потім тренер додасть її собі у групу і ти побачиш розклад занять тут.
            </p>
            <Link to="/me/kids" className="btn-primary inline-flex">+ Додати дитину</Link>
          </div>
        ) : kidsWithoutCoach.length === kids.length ? (
          <div className="card text-center">
            <div className="text-3xl mb-2">⏳</div>
            <h3 className="font-semibold mb-1">Очікуй тренера</h3>
            <p className="text-sm text-tg-hint">
              {kids.length === 1 ? "Дитину" : "Дітей"} додано. Як тільки тренер додасть до своєї групи — заняття з'являться тут.
            </p>
          </div>
        ) : (
          <div className="card text-center">
            <div className="text-3xl mb-2">📅</div>
            <h3 className="font-semibold mb-1">Поки немає занять</h3>
            <p className="text-sm text-tg-hint">Тренер додасть найближче заняття зазвичай день-у-день.</p>
          </div>
        )}
      </Wrapper>
    );
  }

  // Group by day
  const groups = new Map<string, typeof sessions>();
  for (const s of sessions) {
    const k = dateKey(s.starts_at);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(s);
  }

  return (
    <Wrapper>
      <div className="space-y-5">
        {Array.from(groups.entries()).map(([k, list]) => (
          <section key={k}>
            <h2 className="text-tg-hint text-sm uppercase tracking-wide px-1 mb-2">
              {fmtDayLabel(list[0].starts_at)}
            </h2>
            <div className="space-y-2">
              {list.map((s) => {
                const myBookedNames = s.bookings
                  .filter((b) => b.status !== "cancelled" && kidsById.has(b.participant_id))
                  .map((b) => kidsById.get(b.participant_id)!.name);
                return (
                  <SessionCard
                    key={s.id}
                    session={s}
                    to={`/session/${s.id}`}
                    bookedParticipantNames={myBookedNames}
                  />
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </Wrapper>
  );
}

function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">Розклад</h1>
      {children}
    </div>
  );
}
